if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.closest('.product-info').querySelector('.js-inventory-data');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        const svgIcons = {
          backordered: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 16C5.86314 16 3.85403 15.1678 2.34314 13.6569C0.832246 12.146 0 10.1369 0 8C0 5.86314 0.832246 3.85403 2.34314 2.34314C3.85403 0.832246 5.86314 0 8 0C10.1369 0 12.146 0.832246 13.6569 2.34314C15.1678 3.85403 16 5.86314 16 8C16 10.1369 15.1678 12.146 13.6569 13.6569C12.146 15.1678 10.1369 16 8 16ZM7.17957 10.6666C7.02203 10.6666 6.86449 10.6065 6.74437 10.4864L4.28283 8.02486C4.04258 7.78462 4.04258 7.39495 4.28283 7.15446C4.52308 6.91422 4.91274 6.91422 5.15323 7.15446L7.17957 9.1808L10.847 5.51335C11.0873 5.27311 11.4769 5.27311 11.7174 5.51335C11.9579 5.7536 11.9577 6.14326 11.7174 6.38375L7.61477 10.4864C7.49465 10.6065 7.33711 10.6666 7.17957 10.6666Z"  fill="#f5b000"/></svg>',
          very_low: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 16C5.86314 16 3.85403 15.1678 2.34314 13.6569C0.832246 12.146 0 10.1369 0 8C0 5.86314 0.832246 3.85403 2.34314 2.34314C3.85403 0.832246 5.86314 0 8 0C10.1369 0 12.146 0.832246 13.6569 2.34314C15.1678 3.85403 16 5.86314 16 8C16 10.1369 15.1678 12.146 13.6569 13.6569C12.146 15.1678 10.1369 16 8 16ZM7.17957 10.6666C7.02203 10.6666 6.86449 10.6065 6.74437 10.4864L4.28283 8.02486C4.04258 7.78462 4.04258 7.39495 4.28283 7.15446C4.52308 6.91422 4.91274 6.91422 5.15323 7.15446L7.17957 9.1808L10.847 5.51335C11.0873 5.27311 11.4769 5.27311 11.7174 5.51335C11.9579 5.7536 11.9577 6.14326 11.7174 6.38375L7.61477 10.4864C7.49465 10.6065 7.33711 10.6666 7.17957 10.6666Z"  fill="#08b576"/></svg>',
          low: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 16C5.86314 16 3.85403 15.1678 2.34314 13.6569C0.832246 12.146 0 10.1369 0 8C0 5.86314 0.832246 3.85403 2.34314 2.34314C3.85403 0.832246 5.86314 0 8 0C10.1369 0 12.146 0.832246 13.6569 2.34314C15.1678 3.85403 16 5.86314 16 8C16 10.1369 15.1678 12.146 13.6569 13.6569C12.146 15.1678 10.1369 16 8 16ZM7.17957 10.6666C7.02203 10.6666 6.86449 10.6065 6.74437 10.4864L4.28283 8.02486C4.04258 7.78462 4.04258 7.39495 4.28283 7.15446C4.52308 6.91422 4.91274 6.91422 5.15323 7.15446L7.17957 9.1808L10.847 5.51335C11.0873 5.27311 11.4769 5.27311 11.7174 5.51335C11.9579 5.7536 11.9577 6.14326 11.7174 6.38375L7.61477 10.4864C7.49465 10.6065 7.33711 10.6666 7.17957 10.6666Z"  fill="#08b576"/></svg>',
          normal: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 16C5.86314 16 3.85403 15.1678 2.34314 13.6569C0.832246 12.146 0 10.1369 0 8C0 5.86314 0.832246 3.85403 2.34314 2.34314C3.85403 0.832246 5.86314 0 8 0C10.1369 0 12.146 0.832246 13.6569 2.34314C15.1678 3.85403 16 5.86314 16 8C16 10.1369 15.1678 12.146 13.6569 13.6569C12.146 15.1678 10.1369 16 8 16ZM7.17957 10.6666C7.02203 10.6666 6.86449 10.6065 6.74437 10.4864L4.28283 8.02486C4.04258 7.78462 4.04258 7.39495 4.28283 7.15446C4.52308 6.91422 4.91274 6.91422 5.15323 7.15446L7.17957 9.1808L10.847 5.51335C11.0873 5.27311 11.4769 5.27311 11.7174 5.51335C11.9579 5.7536 11.9577 6.14326 11.7174 6.38375L7.61477 10.4864C7.49465 10.6065 7.33711 10.6666 7.17957 10.6666Z"  fill="#08b576"/></svg>',
          in_stock: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 16C5.86314 16 3.85403 15.1678 2.34314 13.6569C0.832246 12.146 0 10.1369 0 8C0 5.86314 0.832246 3.85403 2.34314 2.34314C3.85403 0.832246 5.86314 0 8 0C10.1369 0 12.146 0.832246 13.6569 2.34314C15.1678 3.85403 16 5.86314 16 8C16 10.1369 15.1678 12.146 13.6569 13.6569C12.146 15.1678 10.1369 16 8 16ZM7.17957 10.6666C7.02203 10.6666 6.86449 10.6065 6.74437 10.4864L4.28283 8.02486C4.04258 7.78462 4.04258 7.39495 4.28283 7.15446C4.52308 6.91422 4.91274 6.91422 5.15323 7.15446L7.17957 9.1808L10.847 5.51335C11.0873 5.27311 11.4769 5.27311 11.7174 5.51335C11.9579 5.7536 11.9577 6.14326 11.7174 6.38375L7.61477 10.4864C7.49465 10.6065 7.33711 10.6666 7.17957 10.6666Z"  fill="#08b576"/></svg>',
          none: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>'
        };

       // Determine whether to show the count or not
        let inventoryText = '';
        if (inventoryLevel === 'backordered') {
          inventoryText = "Available";
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          inventoryText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          inventoryText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          inventoryText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          inventoryText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          inventoryText = theme.strings.noStock;
        }

        // Add SVG icon to inventory notice
        this.inventoryNotice.innerHTML = `${svgIcons[inventoryLevel]} ${inventoryText}`;

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = `${svgIcons.very_low} ${this.dataset.textVeryLow}`;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = `${svgIcons.low} ${this.dataset.textLow}`;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = `${svgIcons.backordered} ${this.dataset.textNoStockBackordered}`;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = `${svgIcons.normal} ${this.dataset.textNormal}`;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = `${svgIcons.none} ${this.dataset.textNoStock}`;
          }
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}